import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  docSnapshots,
  collectionChanges,
  getDoc,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  DocumentData,
  Timestamp,
  getDocs,
  documentId,
  collectionSnapshots,
} from '@angular/fire/firestore';
import { Observable, of, from, EMPTY } from 'rxjs';
import { switchMap, mergeMap, map, catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Storage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  StorageReference,
  UploadTask,
  UploadMetadata,
} from '@angular/fire/storage';
import { UserState } from '../components/user/store/user.reducer';
import * as fromUserSelector from '../components/user/store/user.selectors';
import { format, fromUnixTime } from 'date-fns';
import { ContactId } from './../components/contact/store/contact.model';
import { ContactState } from './../components/contact/store/contact.reducer';
import * as fromContactAction from './../components/contact/store/contact.actions';
import * as fromContactSelector from './../components/contact/store/contact.selectors';

@Injectable({
  providedIn: 'any',
})
export class ContactStorageService {
  constructor(
    private db: Firestore,
    private fns: Functions,
    private userStore: Store<UserState>,
    private storage: Storage,
    private contactStore: Store<ContactState>
  ) {}

  getAll(contactId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user) {
            return of([]);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${contactId}/documents`
            );

            const changes = collectionSnapshots(dbCollection).pipe(
              map((changes) => {
                return changes.map((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                });
              })
            );

            return changes;
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  getOne(contactId: string, id: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user) {
            return of(null);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${contactId}/documents`
            );

            const documentRef = doc(dbCollection, id);
            return from(
              getDoc(documentRef)
                .then((snap) => {
                  const data = snap.data();
                  const id = snap.id;
                  return { id, ...data };
                })
                .catch((error) => error)
            ).pipe(mergeMap((res: any) => of(res)));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  addFiles(contact: ContactId, documents: any[]): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !documents?.length) {
            return of(null);
          } else {
            documents.forEach((document) => {
              const { file, doc } = document;
              const extension: string = `${file.name.split('.').pop()}`;
              const storagePah: string = `organisations/${user.organisation.id}/contacts/${contact.id}/documents/${file.name}`;
              const storageRef: StorageReference = ref(
                this.storage,
                storagePah
              );
              const storageMetadata: UploadMetadata = {
                contentType: file.type,
                contentEncoding: '',
                contentLanguage: 'fr',
                cacheControl: '',
                customMetadata: {
                  entiteId: contact.id,
                  organisationId: user.organisation.id,
                  name: file.name,
                  extension: extension,
                  date: new Date(Date.now()).toDateString(),
                },
                md5Hash: '',
              };

              const task: UploadTask = uploadBytesResumable(
                storageRef,
                file,
                storageMetadata
              );
              task.on(
                'state_changed',
                (snap) => {
                  let progress =
                    (snap.bytesTransferred / snap.totalBytes) * 100;
                  this.contactStore.dispatch(
                    fromContactAction.loadFileProgress({ progress })
                  );
                },
                (error) => {
                  this.contactStore.dispatch(
                    fromContactAction.addDocumentsFailure({ error })
                  );
                  this.contactStore.dispatch(
                    fromContactAction.loadFileProgress({ progress: 0 })
                  );
                },
                () => {
                  getDownloadURL(task.snapshot.ref)
                    .then((downloadURL) => {
                      const documentFile = {
                        ...doc,
                        access: {
                          [`${user.id}`]: true,
                        },
                        auteur: user,
                        auths: [user.id],
                        correspondanceId: contact.id,
                        organisation: user.organisation,
                        fileRef: downloadURL,
                        operationId: contact.id,
                        dateStart: Timestamp.now(),
                        file: {
                          name: file.name,
                          path: storagePah,
                          fileName: file.name,
                          size: file.size,
                          extension: file.name.split('.').pop(),
                        },
                      };

                      const documentRef = collection(
                        this.db,
                        `organisations/${user.organisation.id}/contacts/${contact.id}/documents`
                      );
                      const nextDocument = {
                        ...documentFile,
                        dateUpdate: serverTimestamp(),
                        userUpdate: user,
                      };
                      return from(
                        addDoc(documentRef, nextDocument)
                          .then((docRef) => {
                            if (docRef?.id) {
                              this.getOne(contact.id, docRef?.id);
                            } else {
                            }
                          })
                          .then(() => {
                            this.contactStore.dispatch(
                              fromContactAction.loadFileProgress({
                                progress: 0,
                              })
                            );
                            return null;
                          })
                          .catch((error) => of(error))
                      );
                    })
                    .then(() => {
                      this.contactStore.dispatch(
                        fromContactAction.loadDocuments({
                          contactId: contact.id,
                        })
                      );
                    });
                }
              );
              return of(null);
            });

            return of(null);
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  deleteOne(contactId: string, document: any): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !contactId || !document) {
            return of(null);
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/contacts/${contactId}/documents/`
            );

            const dbDocument = doc(dbCollection, document.id);

            return from(
              deleteDoc(dbDocument)
                .then(() => of(`Document supprimé.`))
                .catch((error) => of(error))
            ).pipe(mergeMap((res) => res));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  deleteAll(contactId: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user) {
            return of(null);
          } else {
            const data = { id: user.id };
            const call = httpsCallable(this.fns, 'entite-deleteAllDocuments');
            return from(
              call(data)
                .then((rest) => rest)
                .catch((err) => err)
            ).pipe(
              mergeMap((res) => {
                return of(res);
              })
            );
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }
}
